/**
 * The `portfolio-column` block an Analyst answer ends with, once a column has
 * been added to the table. Drawn by the portfolio page's rail: it tells the
 * page to load the column, then says where it is.
 */

import { Columns3 } from "lucide-react";
import React, { useEffect } from "react";
import type { AddedColumn } from "../../services/portfolioDataService";

export interface AddedColumnCardProps {
	columnId: string;
	column: AddedColumn | null;
	/** The answer is still arriving: do nothing yet. */
	streaming: boolean;
	/** Re-read the tables so the column shows; called once per column. */
	onArrived: (columnId: string) => void;
	onShow: (columnId: string) => void;
}

const AddedColumnCard: React.FC<AddedColumnCardProps> = ({
	columnId,
	column,
	streaming,
	onArrived,
	onShow,
}) => {
	useEffect(() => {
		if (!streaming && columnId) onArrived(columnId);
	}, [streaming, columnId, onArrived]);

	return (
		<div className="my-2 flex items-center gap-3 rounded-xl border border-[#c7d7fe] bg-[#f5f8ff] px-3.5 py-3">
			<Columns3 className="h-5 w-5 flex-shrink-0 text-[#444ce7]" aria-hidden />
			<div className="min-w-0 flex-1">
				<p className="text-[13px] font-semibold text-[#1F2937]">
					{column ? `${column.label} added to the house table` : "Adding the column to the table…"}
				</p>
				{column && (
					<p className="truncate text-xs text-[#5B6472]" title={column.definition}>
						{column.unit} · worked out for every house from your tables
					</p>
				)}
				{!column && !streaming && (
					<p className="text-xs text-[#5B6472]">This column has since been removed, or is still loading.</p>
				)}
			</div>
			{column && (
				<button
					type="button"
					onClick={() => onShow(columnId)}
					className="inline-flex h-8 flex-shrink-0 items-center rounded-lg border border-[#c7d7fe] bg-white px-3 text-[13px] font-medium text-[#3538cd] hover:bg-[#eef4ff]"
				>
					Show in table
				</button>
			)}
		</div>
	);
};

export default AddedColumnCard;
